// The calendar (SCalendar; DECISIONS, Calendar): everything with a date. Month (three a day, then "+N more"), Week
// (a column per day, Friday and Saturday narrower, each day's items as cards: untimed first, then by time) and Day
// (untimed items in a strip at the top, timed ones at their time on the hours). Colour by type or by track; the legend
// hides what you click (remembered). The view and the day are in the address, so Back and a shared link return to them.
// The agenda beside it: the chosen day, then what is coming up. The phone has its own (phone/Calendar.tsx).
// Calendar | List at the top (Kareem, 4 Nov): List is the phone's agenda on the desktop, every meeting and dated item
// grouped by day from today on, with Earlier and Later; which one you use is remembered per person on this browser.
// Week is the main view (6.3, Kareem: "much better this way, week by week"; Calendar.dc.html): it opens on Week unless
// the person last chose another view (remembered per person, like Calendar | List). Sunday to Thursday as cards, each
// headed by its day, date and count; each item a card with its time, owner, title, Space › Track and a prep chip;
// Friday and Saturday behind "Show weekend"; This week and Next week beside the arrows. The space filter is a row of
// buttons when there are a few spaces (a list past five).
import { useActivityDrawer } from '../shell/Dock';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import type { CalendarItem, CalendarKind } from '@dpw/shared';
import { api } from '../api';
import { useCalendar, useMe, useSpaces } from '../lib/queries';
import { useRemembered } from '../lib/theme';
import { Avatar, Diamond, tyBg, tyColor } from '../ui/bits';
import { addDays, dayLabel, localDate, localTime, gbDate, shortDay } from '../lib/format';
import { usePhone } from '../lib/media';
import { PhoneCalendar } from '../phone/Calendar';

export type CalView = 'month' | 'week' | 'day';
export type ColourBy = 'type' | 'track';
const LEGEND: { k: CalendarKind; label: string }[] = [
  { k: 'meeting', label: 'Meeting' }, { k: 'workshop', label: 'Workshop' }, { k: 'deliverable', label: 'Deliverable' },
  { k: 'task', label: 'Task' }, { k: 'reminder', label: 'Reminder' },
];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** Track colours, in turn by first appearance (By track). */
const TRACK_COLOURS = ['var(--t-meet)', 'var(--t-work)', 'var(--t-deliv)', 'var(--t-dead)', 'var(--grn)', 'var(--amb)', 'var(--red)', 'var(--mut)'];
/** The hours the Day view draws (items outside them sit at its edges). */
const FIRST_HOUR = 7, LAST_HOUR = 21, HOUR_PX = 56;

const dow = (d: string) => new Date(`${d}T12:00:00Z`).getUTCDay();
/** The Sunday that starts the week of this day (the team's week runs Sunday to Thursday). */
export const weekStart = (d: string) => addDays(d, -dow(d));
function monthGrid(month: string) {
  const first = `${month}-01`;
  const start = weekStart(first);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}
const isView = (v: string | null): v is CalView => v === 'month' || v === 'week' || v === 'day';
const mins = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
/** Untimed first (a strip in Day), then by time. */
export const byTime = (a: CalendarItem, b: CalendarItem) => (a.start ? 1 : 0) - (b.start ? 1 : 0) || (a.start ?? '').localeCompare(b.start ?? '') || a.title.localeCompare(b.title);
export const typeWord = (i: CalendarItem) => (i.kind === 'reminder' ? (i.aiSet ? 'Reminder set by the assistant' : 'Reminder') : i.kind[0]!.toUpperCase() + i.kind.slice(1));

/** Colours: by type (a reminder the assistant set is violet), or by track (in turn). */
export function useColours(items: CalendarItem[], by: ColourBy) {
  return useMemo(() => {
    const tracks = new Map<string, { name: string; c: string }>();
    for (const i of [...items].sort((a, b) => (a.trackName ?? '').localeCompare(b.trackName ?? ''))) {
      if (i.trackId && !tracks.has(i.trackId)) tracks.set(i.trackId, { name: i.trackName ?? 'Track', c: TRACK_COLOURS[tracks.size % TRACK_COLOURS.length]! });
    }
    const c = (i: CalendarItem) => (by === 'track' ? (i.trackId ? tracks.get(i.trackId)?.c ?? 'var(--mut)' : 'var(--mut)') : i.kind === 'reminder' && i.aiSet ? 'var(--ai)' : tyColor(i.kind));
    const bg = (i: CalendarItem) => (by === 'track' ? 'var(--s2)' : i.kind === 'reminder' && i.aiSet ? 'var(--ai-bg)' : tyBg(i.kind));
    return { c, bg, tracks };
  }, [items, by]);
}

export function CalendarPage() {
  return usePhone() ? <PhoneCalendar /> : <DesktopCalendar />;
}

type CalMode = 'calendar' | 'list';
/**
 * A choice remembered per person on this browser (a convenience: private windows and blocked storage forget it):
 * Calendar or List, and the last view (Week unless they chose another).
 */
function usePersonal<T extends string>(name: string, userId: string | undefined, fallback: T, valid: (v: string | null) => v is T): [T, (m: T) => void] {
  const key = `dpw.calendar.${name}.${userId ?? 'anon'}`;
  const read = (k: string): T => { try { const v = localStorage.getItem(k); return valid(v) ? v : fallback; } catch { return fallback; } };
  const [state, setState] = useState<{ key: string; v: T }>(() => ({ key, v: read(key) }));
  // The person is known after the first paint on a cold load: read their own choice then.
  const v = state.key === key ? state.v : read(key);
  const set = useCallback((m: T) => {
    setState({ key, v: m });
    try { localStorage.setItem(key, m); } catch { /* storage blocked: it still switches for now */ }
  }, [key]);
  return [v, set];
}
const isMode = (v: string | null): v is CalMode => v === 'calendar' || v === 'list';
const useCalMode = (userId: string | undefined) => usePersonal<CalMode>('mode', userId, 'calendar', isMode);

function DesktopCalendar() {
  const me = useMe();
  const [mode, setMode] = useCalMode(me.data?.id);
  const today = localDate();
  const [params, setParams] = useSearchParams();
  const [lastView, setLastView] = usePersonal<CalView>('view', me.data?.id, 'week', isView);
  const view: CalView = isView(params.get('view')) ? params.get('view') as CalView : lastView;
  const sel = /^\d{4}-\d{2}-\d{2}$/.test(params.get('date') ?? '') ? params.get('date')! : today;
  const go = (v: CalView, d: string) => {
    if (v !== view) setLastView(v);
    setParams(p => { p.set('view', v); p.set('date', d); return p; }, { replace: true });
  };
  const [weekend, setWeekend] = useRemembered('dpw.calendar.weekend', false);
  const [spaceId, setSpaceId] = useRemembered('dpw.calendar.space', '');
  const [mine, setMine] = useRemembered('dpw.calendar.mine', false);
  const [by, setBy] = useRemembered<ColourBy>('dpw.calendar.by', 'type');
  const [hidden, setHidden] = useRemembered<CalendarKind[]>('dpw.calendar.hidden', []);
  const [hiddenTracks, setHiddenTracks] = useRemembered<string[]>('dpw.calendar.hiddenTracks', []);
  const spaces = useSpaces();
  const month = sel.slice(0, 7);
  const grid = useMemo(() => monthGrid(month), [month]);
  const ws = weekStart(sel);
  // What the screen shows, plus two weeks for "Coming up".
  const [from, to] = view === 'month' ? [grid[0]!, grid[41]!] : view === 'week' ? [ws, addDays(ws, 20)] : [sel, addDays(sel, 14)];
  const q = useCalendar(from, to, spaceId || undefined, mine);
  const nav = useNavigate();
  const all = q.data ?? [];
  // The legend's tracks are the ones on screen (the fetch also covers Coming up).
  const shownDays = view === 'month' ? [grid[0]!, grid[41]!] : view === 'week' ? [ws, addDays(ws, weekend ? 6 : 4)] : [sel, sel];
  const colours = useColours(all.filter(i => i.date >= shownDays[0]! && i.date <= shownDays[1]!), by);
  const visible = all.filter(i => !hidden.includes(i.kind) && !(i.trackId && hiddenTracks.includes(i.trackId)));
  const byDay = useMemo(() => {
    const m = new Map<string, CalendarItem[]>();
    for (const i of visible) m.set(i.date, [...(m.get(i.date) ?? []), i]);
    for (const l of m.values()) l.sort(byTime);
    return m;
  }, [visible]);
  const shift = (n: number) => {
    if (view === 'day') return go(view, addDays(sel, n));
    if (view === 'week') return go(view, addDays(sel, 7 * n));
    const d = new Date(`${month}-15T12:00:00Z`);
    d.setUTCMonth(d.getUTCMonth() + n);
    go(view, `${d.toISOString().slice(0, 7)}-01`);
  };
  /** Every "open this activity" on the calendar (Month, Week, Day and List) goes through here: the activity panel
   *  beside the calendar (6.2). */
  const acts = useActivityDrawer();
  const openActivity = (id: string) => acts.open(id);
  const open = (i: CalendarItem) => (i.kind === 'reminder' || !i.activityId ? nav('/reminders') : openActivity(i.activityId));
  const period = view === 'month' ? gbDate(new Date(`${month}-15T12:00:00Z`), { month: 'long', year: 'numeric', timeZone: 'UTC' })
    : view === 'week' ? `${gbDate(new Date(`${ws}T12:00:00Z`), { day: 'numeric', month: 'short', timeZone: 'UTC' })} to ${gbDate(new Date(`${addDays(ws, weekend ? 6 : 4)}T12:00:00Z`), { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}`
    : gbDate(new Date(`${sel}T12:00:00Z`), { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
  const weekendCount = [5, 6].reduce((n, k) => n + (byDay.get(addDays(ws, k))?.length ?? 0), 0);
  const agenda = byDay.get(sel) ?? [];
  const next = visible.filter(i => i.date > sel && !i.done).sort((a, b) => a.date.localeCompare(b.date) || byTime(a, b)).slice(0, 6);
  const unit = view === 'month' ? 'month' : view === 'week' ? 'week' : 'day';

  return (
    <>
      <div className="hdr">
        <div className="htl tb">
          <h1>Calendar</h1>
          <div className="seg2" role="group" aria-label="Show as">
            {(['calendar', 'list'] as const).map(m => <button key={m} type="button" className={mode === m ? 'on' : ''} aria-pressed={mode === m} onClick={() => setMode(m)}>{m === 'calendar' ? 'Calendar' : 'List'}</button>)}
          </div>
          {mode === 'calendar' && <>
            <button className="ib" onClick={() => shift(-1)} aria-label={`Previous ${unit}`}>‹</button>
            <button className="ib" onClick={() => shift(1)} aria-label={`Next ${unit}`}>›</button>
            <span className="mo" aria-live="polite">{period}</span>
            <button className="bt sm" onClick={() => go(view, today)}>Today</button>
            {view === 'week' && <div className="seg2" role="group" aria-label="Which week">
              {([['This week', weekStart(today)], ['Next week', addDays(weekStart(today), 7)]] as const).map(([l, d]) => (
                <button key={l} type="button" className={ws === d ? 'on' : ''} aria-pressed={ws === d} onClick={() => go('week', d)}>{l}</button>
              ))}
            </div>}
            {view === 'week' && <button type="button" className={`bt sm${weekend ? ' on' : ''}`} aria-pressed={weekend} onClick={() => setWeekend(!weekend)}
              title="Friday and Saturday">Show weekend{!weekend && weekendCount ? ` (${weekendCount})` : ''}</button>}
            <div className="seg2" role="group" aria-label="View">
              {(['month', 'week', 'day'] as const).map(v => <button key={v} type="button" className={view === v ? 'on' : ''} aria-pressed={view === v} onClick={() => go(v, sel)}>{v[0]!.toUpperCase() + v.slice(1)}</button>)}
            </div>
          </>}
        </div>
        <div className="hact tb">
          {(spaces.data ?? []).length <= 5 ? (
            <div className="seg2" role="group" aria-label="Space">
              {[{ id: '', name: 'All spaces' }, ...(spaces.data ?? [])].map(s => (
                <button key={s.id} type="button" className={spaceId === s.id ? 'on' : ''} aria-pressed={spaceId === s.id} onClick={() => setSpaceId(s.id)}>{s.name}</button>
              ))}
            </div>
          ) : (
            <select className="inp" value={spaceId} onChange={e => setSpaceId(e.target.value)} aria-label="Space">
              <option value="">All spaces</option>{(spaces.data ?? []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          <div className="seg2"><button className={mine ? 'on' : ''} onClick={() => setMine(!mine)} aria-pressed={mine}>Mine only</button></div>
          <div className="seg2" role="group" aria-label="Colour by">
            <button type="button" className={by === 'type' ? 'on' : ''} aria-pressed={by === 'type'} onClick={() => setBy('type')}>By type</button>
            <button type="button" className={by === 'track' ? 'on' : ''} aria-pressed={by === 'track'} onClick={() => setBy('track')}>By track</button>
          </div>
        </div>
      </div>
      <div className="legend" aria-label="Filters">
        {by === 'type' ? <>
          {LEGEND.map(l => (
            <button key={l.k} className={`lg${hidden.includes(l.k) ? ' off' : ''}`} style={{ ['--c' as string]: tyColor(l.k) }} aria-pressed={!hidden.includes(l.k)}
              onClick={() => setHidden(hidden.includes(l.k) ? hidden.filter(x => x !== l.k) : [...hidden, l.k])}><i />{l.label}</button>
          ))}
          <span className="lg" style={{ ['--c' as string]: 'var(--ai)', cursor: 'default' }}><i />Set by the assistant</span>
        </> : [...colours.tracks].map(([id, t]) => (
          <button key={id} className={`lg${hiddenTracks.includes(id) ? ' off' : ''}`} style={{ ['--c' as string]: t.c }} aria-pressed={!hiddenTracks.includes(id)}
            onClick={() => setHiddenTracks(hiddenTracks.includes(id) ? hiddenTracks.filter(x => x !== id) : [...hiddenTracks, id])}><i />{t.name}</button>
        ))}
        <span className="lg" style={{ ['--c' as string]: 'var(--t-deliv)', cursor: 'default' }}><Diamond sm />Milestone</span>
        <span className="xs mut" style={{ marginLeft: 6 }}>Click a colour to hide it</span>
      </div>
      {mode === 'list' ? <CalendarList spaceId={spaceId} mine={mine} by={by} hidden={hidden} hiddenTracks={hiddenTracks} today={today} onOpen={open} />
        : view === 'week' ? <WeekView ws={ws} today={today} sel={sel} byDay={byDay} colour={colours.c} weekend={weekend} loading={q.isLoading}
          onDay={d => go('day', d)} onOpen={open} />
        : <div className="calwrap">
        {view === 'month' && (
          <div className="month" role="grid" aria-label={period}>
            {DOW.map(d => <div className="mhd" key={d}>{d}</div>)}
            {grid.map(d => {
              const items = byDay.get(d) ?? [];
              const out = d.slice(0, 7) !== month;
              return (
                <div key={d} className={`cell${out ? ' out' : ''}${d === today ? ' today' : ''}${d === sel ? ' sel' : ''}`} role="gridcell">
                  <button className="dnb" onClick={() => go('month', d)} onDoubleClick={() => go('day', d)} aria-label={dayLabel(d)}>{Number(d.slice(8))}</button>
                  {items.slice(0, 3).map(i => (
                    <button key={`${i.kind}-${i.id}`} className={`cev${i.done ? ' done' : ''}`} style={{ ['--c' as string]: colours.c(i), ['--cb' as string]: colours.bg(i) }} onClick={() => open(i)} title={i.title}>
                      {i.isMilestone && <Diamond sm />}
                      {i.start && <span className="tm">{i.start}</span>}
                      <span className="tt">{i.waitingOn ? `${i.title} · Waiting on ${i.waitingOn}` : i.title}</span>
                    </button>
                  ))}
                  {items.length > 3 && <button className="more" onClick={() => go('month', d)}>+{items.length - 3} more</button>}
                </div>
              );
            })}
          </div>
        )}
        {view === 'day' && <DayView day={sel} items={byDay.get(sel) ?? []} colour={colours.c} today={today} onOpen={open} />}
        <aside className="agenda">
          <div className="agh"><div className="xs mut">{sel === today ? 'Today' : 'Selected day'}</div><b>{dayLabel(sel)}</b></div>
          <div className="agb">
            {q.isLoading && <span className="xs mut">Loading</span>}
            {!agenda.length && !q.isLoading && <span className="xs mut" style={{ padding: 6 }}>Nothing on this day.</span>}
            {agenda.map(i => <AgendaRow key={`${i.kind}-${i.id}`} i={i} c={colours.c(i)} when={i.start ?? 'All day'} onOpen={() => open(i)} />)}
            {next.length > 0 && <div className="agsec">Coming up</div>}
            {next.map(i => <AgendaRow key={`n-${i.kind}-${i.id}`} i={i} c={colours.c(i)} when={gbDate(new Date(`${i.date}T12:00:00Z`), { day: 'numeric', month: 'short', timeZone: 'UTC' })} onOpen={() => open(i)} />)}
          </div>
        </aside>
      </div>}
    </>
  );
}

/** "Thursday 29 Oct" */
export const longDay = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short', timeZone: 'UTC' }).replace(/\bSept\b/, 'Sep').replace(',', '');
/** How far the list reaches each time it loads more (days; the API gives at most 62 a request). */
const LIST_STEP = 28;

/**
 * The List (the phone's agenda, on the desktop): every meeting and dated item you can see, by day, from today on.
 * Earlier loads the four weeks before; Later the four after. The filters above (space, mine, colours) apply.
 */
function CalendarList({ spaceId, mine, by, hidden, hiddenTracks, today, onOpen }: {
  spaceId: string; mine: boolean; by: ColourBy; hidden: CalendarKind[]; hiddenTracks: string[]; today: string; onOpen: (i: CalendarItem) => void;
}) {
  const [blocks, setBlocks] = useState({ lo: 0, hi: 1 });
  const q = useQueries({
    queries: Array.from({ length: blocks.hi - blocks.lo + 1 }, (_, n) => {
      const from = addDays(today, (blocks.lo + n) * LIST_STEP), to = addDays(from, LIST_STEP - 1);
      return { queryKey: ['calendar', from, to, spaceId || undefined, mine], queryFn: () => api.calendar({ from, to, spaceId: spaceId || undefined, mine: mine ? 'true' : undefined }) };
    }),
    combine: rs => ({ items: rs.flatMap(x => x.data ?? []), loading: rs.some(x => x.isLoading), error: rs.find(x => x.error)?.error ?? null }),
  });
  const from = addDays(today, blocks.lo * LIST_STEP), to = addDays(today, blocks.hi * LIST_STEP + LIST_STEP - 1);
  const colours = useColours(q.items, by);
  const days = useMemo(() => {
    const m = new Map<string, CalendarItem[]>();
    for (const i of q.items) if (!hidden.includes(i.kind) && !(i.trackId && hiddenTracks.includes(i.trackId))) m.set(i.date, [...(m.get(i.date) ?? []), i]);
    for (const l of m.values()) l.sort(byTime);
    return [...m].sort((a, b) => a[0].localeCompare(b[0]));
  }, [q.items, hidden, hiddenTracks]);
  return (
    <div className="panel callist" role="feed" aria-label="Agenda" aria-busy={q.loading}>
      <div className="clmore">
        <span className="xs mut">{blocks.lo < 0 ? `From ${dayLabel(from)}` : 'From today'}</span>
        <button type="button" className="bt sm" onClick={() => setBlocks(b => ({ ...b, lo: b.lo - 1 }))} disabled={q.loading}>Earlier</button>
      </div>
      {q.loading && !q.items.length && <div className="xs mut clnone">Loading</div>}
      {!q.loading && !days.length && <div className="sm mut clnone">Nothing on the calendar from {dayLabel(from)} to {dayLabel(to)}{hidden.length || hiddenTracks.length ? '. Some colours are hidden: click one to show it.' : mine ? ' that is yours.' : '.'}</div>}
      {days.map(([d, items]) => (
        <section key={d} className={`clday${d < today ? ' past' : ''}`} data-day={d} role="article" aria-label={longDay(d)}>
          <h2 className="cldh">{longDay(d)}{d === today && <span className="tdy">Today</span>}</h2>
          {items.map(i => (
            <button key={`${i.kind}-${i.id}`} type="button" className={`clrow${i.done ? ' done' : ''}`} style={{ ['--c' as string]: colours.c(i) }} onClick={() => onOpen(i)} data-kind={i.kind}>
              <span className="cltm mono">{i.start ? <>{i.start}{i.end && <span className="mut">{i.end}</span>}</> : <span className="mut">All day</span>}</span>
              <span className="clbar" aria-hidden="true" />
              <span className="clt">{i.isMilestone && <Diamond sm />}{i.title}</span>
              <span className="clm xs mut">{[typeWord(i), i.trackName, i.spaceName, i.owner, i.waitingOn ? `Waiting on ${i.waitingOn}` : null].filter(Boolean).join(' · ')}</span>
            </button>
          ))}
        </section>
      ))}
      <div className="clmore">
        <span className="xs mut">Up to {dayLabel(to)}</span>
        <button type="button" className="bt sm" onClick={() => setBlocks(b => ({ ...b, hi: b.hi + 1 }))} disabled={q.loading}>Later</button>
      </div>
    </div>
  );
}

function AgendaRow({ i, c, when, onOpen }: { i: CalendarItem; c: string; when: string; onOpen: () => void }) {
  return (
    <button className="agi" style={{ ['--c' as string]: c }} onClick={onOpen}>
      <span className="tm">{when}</span>
      <span>
        <span className="t">{i.isMilestone ? <Diamond sm /> : <i />}{i.title}</span>
        <span className="m" style={{ display: 'block' }}>{[typeWord(i), i.start && when !== i.start ? i.start : null, i.trackName, i.owner, i.waitingOn ? `Waiting on ${i.waitingOn}` : null, i.end ? `until ${i.end}` : null].filter(Boolean).join(' · ')}</span>
      </span>
    </button>
  );
}

/** The next working day after this one (the team's week is Sunday to Thursday). */
function nextWorkingDay(d: string) {
  let n = addDays(d, 1);
  while (dow(n) >= 5) n = addDays(n, 1);
  return n;
}

/**
 * The prep chip on a Week card, from what the activity has: needs prep, its steps done of total, and its status.
 * Red when prep is not finished and the item is today or the next working day (red means risk).
 */
export function prepChip(i: CalendarItem, today: string): { label: string; tone: 'neu' | 'amb' | 'grn' | 'red' } | null {
  if (i.kind === 'reminder') return null;
  if (i.done) return { label: 'Done', tone: 'grn' };
  if (!i.needsPrep) return { label: 'No prep', tone: 'neu' };
  if (i.stepsTotal > 0 && i.stepsDone === i.stepsTotal) return { label: 'Ready', tone: 'grn' };
  const pct = i.stepsTotal ? Math.round((i.stepsDone / i.stepsTotal) * 100) : 0;
  const soon = i.date >= today && i.date <= nextWorkingDay(today);
  return { label: pct ? `Prep ${pct}%` : 'Prep not started', tone: soon ? 'red' : pct ? 'amb' : 'neu' };
}

/**
 * The Week view (Calendar.dc.html): a card per day, Sunday to Thursday (Friday and Saturday too with Show weekend),
 * headed by the day, the date and how many items ("Free" when none); today stands out. The header opens the day.
 */
function WeekView({ ws, today, sel, byDay, colour, weekend, loading, onDay, onOpen }: {
  ws: string; today: string; sel: string; byDay: Map<string, CalendarItem[]>; colour: (i: CalendarItem) => string; weekend: boolean; loading: boolean;
  onDay: (d: string) => void; onOpen: (i: CalendarItem) => void;
}) {
  const days = Array.from({ length: 7 }, (_, n) => addDays(ws, n)).filter(d => weekend || dow(d) < 5);
  return (
    <div className="weekg wk" role="list" aria-label={`Week of ${dayLabel(ws)}`} aria-busy={loading} style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}>
      {days.map(d => {
        const items = byDay.get(d) ?? [];
        return (
          <div key={d} role="listitem" aria-label={dayLabel(d)} data-day={d} className={`cwd${dow(d) >= 5 ? ' we' : ''}${d === today ? ' today' : ''}${d === sel ? ' sel' : ''}`}>
            <button type="button" className="dh" onClick={() => onDay(d)} aria-label={`Open ${dayLabel(d)}`}>
              <span className="dhl"><span className="eb">{DOW[dow(d)]}{d === today && <span className="tdy">Today</span>}</span><b>{shortDay(d)}</b></span>
              <span className="cnt">{items.length ? `${items.length} ${items.length === 1 ? 'item' : 'items'}` : 'Free'}</span>
            </button>
            {items.map(i => <WeekCard key={`${i.kind}-${i.id}`} i={i} c={colour(i)} today={today} onOpen={() => onOpen(i)} />)}
          </div>
        );
      })}
    </div>
  );
}

/** One item: its time and owner, the title, Space › Track, its type and (in Week) the prep chip. */
function WeekCard({ i, c, onOpen, today }: { i: CalendarItem; c: string; onOpen: () => void; today?: string }) {
  const prep = today ? prepChip(i, today) : null;
  const where = [i.spaceName, i.trackName].filter(Boolean).join(' › ');
  return (
    <button type="button" className={`cwcard${i.done ? ' done' : ''}`} style={{ ['--c' as string]: c }} onClick={onOpen} data-kind={i.kind}>
      <span className="rowc wtop"><span className="xs mono mut">{i.start ? `${i.start}${i.end ? `-${i.end}` : ''}` : 'All day'}</span>
        <span className="sp" />{i.owner && <Avatar p={{ name: i.owner, initials: i.ownerInitials }} />}</span>
      <span className="t">{i.isMilestone && <Diamond sm />}{i.title}</span>
      {where && <span className="m">{where}</span>}
      {i.waitingOn && <span className="m">Waiting on {i.waitingOn}</span>}
      <span className="rowc" style={{ gap: 6 }}><span className="wty"><i />{i.isMilestone ? 'Milestone' : typeWord(i)}</span>
        {prep && <span className={`pl ${prep.tone} prep`}>{prep.label}</span>}</span>
    </button>
  );
}

/** One day: untimed items in a strip at the top, timed ones placed at their time on the hours (overlaps side by side). */
function DayView({ day, items, colour, today, onOpen }: { day: string; items: CalendarItem[]; colour: (i: CalendarItem) => string; today: string; onOpen: (i: CalendarItem) => void }) {
  const untimed = items.filter(i => !i.start);
  const timed = items.filter(i => i.start).map(i => {
    const s = Math.min(Math.max(mins(i.start!), FIRST_HOUR * 60), LAST_HOUR * 60 - 30);
    const e = Math.max(s + 30, Math.min(i.end ? mins(i.end) : s + 30, LAST_HOUR * 60));
    return { i, s, e };
  });
  // Side by side: each item takes the first column free at its start.
  const cols: number[] = [];
  const placed = timed.map(t => {
    let col = cols.findIndex(end => end <= t.s);
    if (col < 0) { col = cols.length; cols.push(t.e); } else cols[col] = t.e;
    return { ...t, col };
  });
  const width = Math.max(1, cols.length);
  const hours = Array.from({ length: LAST_HOUR - FIRST_HOUR }, (_, n) => FIRST_HOUR + n);
  const nowM = mins(localTime(new Date().toISOString()));
  return (
    <div className="dayv" role="region" aria-label={dayLabel(day)}>
      <div className="dstrip" role="list" aria-label="All day">
        <span className="k">All day</span>
        {untimed.map(i => <span role="listitem" key={`${i.kind}-${i.id}`}><WeekCard i={i} c={colour(i)} onOpen={() => onOpen(i)} /></span>)}
        {!untimed.length && <span className="xs mut">Nothing without a time.</span>}
      </div>
      <div className="dgrid" style={{ height: hours.length * HOUR_PX }} role="list" aria-label="By time">
        {hours.map(h => <div key={h} className="dhr" style={{ top: (h - FIRST_HOUR) * HOUR_PX }}><span className="mono xs mut">{String(h).padStart(2, '0')}:00</span></div>)}
        {day === today && nowM >= FIRST_HOUR * 60 && nowM <= LAST_HOUR * 60 && <div className="dnow" style={{ top: ((nowM - FIRST_HOUR * 60) / 60) * HOUR_PX }} aria-hidden="true" />}
        {placed.map(p => {
          const h = Math.max(26, ((p.e - p.s) / 60) * HOUR_PX - 2);
          return (
            <button key={`${p.i.kind}-${p.i.id}`} type="button" role="listitem" className={`dev${p.i.done ? ' done' : ''}`} onClick={() => onOpen(p.i)} data-kind={p.i.kind} title={p.i.title}
              style={{ ['--c' as string]: colour(p.i), top: ((p.s - FIRST_HOUR * 60) / 60) * HOUR_PX, height: h,
                left: `calc(56px + (100% - 60px) * ${p.col / width})`, width: `calc((100% - 60px) / ${width} - 4px)` }}>
              <span className="l1"><span className="mono">{p.i.start}{p.i.end ? `-${p.i.end}` : ''}</span> {p.i.isMilestone ? 'Milestone: ' : ''}{p.i.title}</span>
              {h >= 46 && <span className="l2">{[typeWord(p.i), p.i.trackName, p.i.owner].filter(Boolean).join(' · ')}</span>}
            </button>
          );
        })}
        {!timed.length && <span className="xs mut dnone">Nothing at a time on this day.</span>}
      </div>
    </div>
  );
}
